import { useEffect, useState } from "react";
import { Alert, Box, CircularProgress, Skeleton } from "@mui/material";
import { fetchRunOutputBlobUrl, revokeRunOutputBlobUrl } from "../utils/api";

function looksLikeImageUrl(s) {
  if (typeof s !== "string") return false;
  return /\.(png|jpe?g|gif|webp|svg)(\?|$)/i.test(s) || /\/image\//i.test(s);
}

/**
 * Loads a run-scoped GCS asset through the authenticated API (same pattern as notebook fetch).
 * @param {{ runId: string; url?: string; path?: string; alt?: string; maxHeight?: number|string }} props
 */
export default function AuthenticatedRunAssetImage({ runId, url, path, alt = "", maxHeight = 360 }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    let current = null;

    async function load() {
      if (!runId || (!url && !path)) {
        setLoading(false);
        return;
      }
      setErr("");
      setLoading(true);
      try {
        const b = await fetchRunOutputBlobUrl(runId, { url, path });
        if (!alive) {
          revokeRunOutputBlobUrl(b);
          return;
        }
        current = b;
        setBlobUrl(b);
      } catch (e) {
        if (alive) setErr(e?.message || "Failed to load image");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => {
      alive = false;
      if (current) revokeRunOutputBlobUrl(current);
    };
  }, [runId, url, path]);

  if (!runId || (!url && !path)) return null;

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={2} py={2}>
        <CircularProgress size={28} />
        <Skeleton variant="rounded" width="100%" height={Math.min(Number(maxHeight) || 360, 400)} />
      </Box>
    );
  }

  if (err) {
    return (
      <Alert severity="warning" sx={{ my: 1 }}>
        {err}
      </Alert>
    );
  }

  if (!blobUrl) return null;

  return (
    <Box
      component="img"
      src={blobUrl}
      alt={alt}
      sx={{
        maxWidth: "100%",
        height: "auto",
        maxHeight,
        borderRadius: 2,
        display: "block",
        border: "1px solid",
        borderColor: "divider",
      }}
    />
  );
}

export { looksLikeImageUrl };
